import { useEffect, useState } from "react";
import styled from "styled-components";

import { LinkButton } from "@components";
import { Organization, OrgData } from "@types";
import { withToaster } from "@utils";

import { SettingSwitch } from "@pages/projects/components";
import {
  fetchOrganizations,
  patchOrgName,
  postHasAutofix,
} from "@pages/settings/api";

const Container = styled.div`
  width: 600px;
  padding-top: 20px;
`;

interface Props {
  deployment: OrgData;
}

export const DeploymentInfoPanel: React.FC<Props> = ({ deployment }) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [parentOrganization, setParentOrganization] = useState<
    Organization | undefined
  >(undefined);

  const promptForRename = (parentOrg: Organization) => {
    const newName = window.prompt("Rename the organization:", parentOrg.name);
    if (!newName) return;

    withToaster(
      () =>
        patchOrgName(parentOrg.id, newName).then(() => {
          setParentOrganization(undefined); // force a refresh
        }),
      (err) => `Could not update organization: ${err.body?.error}`
    )();
  };

  useEffect(() => {
    let isMounted = true;
    if (!deployment || !(parentOrganization === undefined)) {
      return;
    }
    withToaster(
      () =>
        fetchOrganizations().then((organizations) => {
          if (!isMounted) return;
          const relevantOrg = organizations.filter(
            (o) => o.id === deployment?.organization_id
          )[0];
          setParentOrganization(relevantOrg);
        }),
      (err) => `Failed to load organizations: ${err.body?.error}`
    )().finally(() => {
      if (!isMounted) return;
    });

    // Abort any callbacks/promises on component unmounting
    return () => {
      isMounted = false;
    };
  }, [deployment, parentOrganization]);

  const updateAutofix = async (new_autofix: boolean) => {
    setIsSaving(true);

    postHasAutofix(deployment.id, new_autofix)
      .then((r) => {
        deployment.has_autofix = r.has_autofix;
      })
      .catch((err) => `Could not update autofix: ${err.body?.error}`)
      .finally(() => setIsSaving(false));
  };

  // Show the list only if the actual Organization has multiple deployments and can use the information
  const isShowingDeploymentInOrgList =
    parentOrganization &&
    !!parentOrganization.deployments &&
    parentOrganization.deployments.length > 1;

  return (
    <Container>
      <p>
        Your deployment ID for {deployment.name} is{" "}
        {deployment?.id || "not defined"}.
        <br />
        {/* TODO: Allow users to change their deployment slug (once we migrate FE to it fully) */}
        {deployment?.slug ? (
          <>
            {" "}
            Your deployment slug (computer-friendly name) is{" "}
            <code style={{ color: "red" }}>{deployment.slug}</code>.
            <br />{" "}
          </>
        ) : null}
        {parentOrganization && (
          <>
            Your deployment is a part of your organization{" "}
            {parentOrganization.name}.
            <sup>
              <LinkButton onClick={() => promptForRename(parentOrganization)}>
                rename
              </LinkButton>
            </sup>
            <br />{" "}
          </>
        )}
        This data uniquely identifies your organization. If you would like to
        view data for a different organization that you belong to, you can
        change your deployment in the top-left corner.
      </p>
      {isShowingDeploymentInOrgList && (
        <>
          <p>All deployments in {parentOrganization.name} organization:</p>
          <ul>
            {parentOrganization.deployments!.map((d) => (
              <li key={d.id}>
                {d.name} - {d.id}
              </li>
            ))}
          </ul>
        </>
      )}
      <SettingSwitch
        title={"Autofix"}
        description={"Include autofix suggestions in pull request comments."}
        warning={
          "Your autofixed code will be sent to Semgrep, but it will not be stored."
        }
        value={deployment.has_autofix}
        onChange={() => updateAutofix(!deployment.has_autofix)}
        disabled={isSaving}
      />
    </Container>
  );
};
